import ScreenTitle from '../../../../common/components/screenTitle/ScreenTitle';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './NotificationsTab.module.scss';
import { AlarmTriggerDto } from 'api/alarms/models/AlarmTriggersDto';
import DateFormat from 'common/components/dateFormat/dateFormat';
import moment from 'moment';
import { DATE_TIME_FORMAT_DEFAULT, DefaultAssetImage, DEFAULT_PAGINATION_ITEMS_PER_PAGE, MAX_PAGINATION_ITEMS_PER_PAGE, LOGGER_LOG_TYPE } from 'Config';
import { AlarmsSearchCriteria } from 'api/alarms/models/AlarmsSearchCriteria';
import Loading from 'common/services/Loading';
import AlarmsService from 'api/alarms/AlarmsService';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';
import { FaExternalLinkAlt, FaMapMarkerAlt } from 'react-icons/fa';
import DataTableWithSidebar from 'common/components/dataTable/DataTableWithSidebar';
import { DataTableColumn, OrderData } from 'common/components/dataTable/models';
import useBidirectionalScroll from 'common/hooks/useBidirectionalScroll';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';

export type Props = {
    alarmId: string | undefined
} & React.DetailedHTMLProps<React.LabelHTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const NotificationsTab = ({ alarmId }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const [triggers, setTriggers] = useState<AlarmTriggerDto[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const isLoadingData = useRef(false);
    const imageCacheKey = useRef(new Date().getTime());
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);

    const [criteria, setCriteria] = useState<AlarmsSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'ti.date',
        orderBy: 'desc',
        alarmId: alarmId,
    });

    const [order, setOrder] = useState<OrderData<AlarmTriggerDto>>({
        field: 'triggerDate' as keyof AlarmTriggerDto,
        orderColumn: 'ti.date',
        isOrderAsc: false,
    });

    const {
        loadedPageRange,
        isLoadingPrevious,
        shouldLoadAdjacentPages,
        setLoadedPageRange,
        setIsLoadingPrevious,
        resetScrollState,
        prepareLoadPrevious,
        adjustScrollAfterPrepend,
        finishRestoration,
        getHasMore,
        getHasMorePrevious,
        getOnLoadMore,
        getOnLoadPrevious,
    } = useBidirectionalScroll({
        cacheKey: 'ALARMSTRIGGERSFILTER',
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
    });

    useEffect(() => {
        getData(criteria.page > 1);
    }, [criteria]);

    const getData = async (isLoadMore = false) => {
        if (isLoadingData.current) return;
        isLoadingData.current = true;

        try {
            if (!isLoadMore) {
                Loading.show();
            } else {
                setIsLoadingMore(true);
            }

            const result = await AlarmsService.getAlarmTriggersList(criteria);

            if (isLoadMore) {
                setTriggers(prev => [...prev, ...(result.items ?? [])]);
                setLoadedPageRange(prev => ({ ...prev, max: criteria.page }));
            } else {
                setTriggers(result.items ?? []);
            }
            setTotalItems(result.totalItems);

            // Handle bidirectional scroll restoration
            if (shouldLoadAdjacentPages.current) {
                shouldLoadAdjacentPages.current = false;
                const currentPage = criteria.page;
                const totalPages = Math.ceil(result.totalItems / DEFAULT_PAGINATION_ITEMS_PER_PAGE);

                if (currentPage > 1) {
                    try {
                        const prevCriteria = { ...criteria, page: currentPage - 1 };
                        const prevResult = await AlarmsService.getAlarmTriggersList(prevCriteria);
                        setTriggers(prev => [...(prevResult.items ?? []), ...prev]);
                        setLoadedPageRange(prev => ({ ...prev, min: currentPage - 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.ALARMS, `Couldn't load previous triggers page`, e);
                    }
                }

                if (currentPage < totalPages) {
                    try {
                        const nextCriteria = { ...criteria, page: currentPage + 1 };
                        const nextResult = await AlarmsService.getAlarmTriggersList(nextCriteria);
                        setTriggers(prev => [...prev, ...(nextResult.items ?? [])]);
                        setLoadedPageRange(prev => ({ ...prev, max: currentPage + 1 }));
                    } catch (e) {
                        Logger.error(LOGGER_LOG_TYPE.ALARMS, `Couldn't load next triggers page`, e);
                    }
                }

                finishRestoration();
            }
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.ALARMS, `Couldn't get alarms triggers list`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            resetScrollState();
        } finally {
            Loading.hide();
            setIsLoadingMore(false);
            isLoadingData.current = false;
        }
    };

    const loadMore = useCallback(() => {
        if (isLoadingData.current || isLoadingMore || triggers.length >= totalItems) return;
        const nextPage = loadedPageRange.max + 1;
        setCriteria(prev => ({ ...prev, page: nextPage }));
    }, [isLoadingMore, triggers.length, totalItems, loadedPageRange.max]);

    const loadPrevious = useCallback(async () => {
        if (isLoadingPrevious || loadedPageRange.min <= 1) return;

        setIsLoadingPrevious(true);
        const prevPage = loadedPageRange.min - 1;

        try {
            prepareLoadPrevious();
            const prevCriteria: AlarmsSearchCriteria = {
                ...criteria,
                page: prevPage,
                itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
            };
            const result = await AlarmsService.getAlarmTriggersList(prevCriteria);

            setTriggers(prev => [...(result.items ?? []), ...prev]);
            setLoadedPageRange(prev => ({ ...prev, min: prevPage }));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.ALARMS, `Couldn't get previous triggers page`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        } finally {
            setIsLoadingPrevious(false);
        }
    }, [isLoadingPrevious, loadedPageRange.min, criteria, addToast, t]);

    useEffect(() => {
        if (!isLoadingPrevious) {
            adjustScrollAfterPrepend();
        }
    }, [triggers, isLoadingPrevious, adjustScrollAfterPrepend]);

    const handleOrderChange = (orderData: OrderData<AlarmTriggerDto>) => {
        setOrder(orderData);
        setTriggers([]);
        resetScrollState();
        setTotalItems(0);
        setCriteria(prev => ({
            ...prev,
            page: 1,
            orderBy: orderData.isOrderAsc ? 'asc' : 'desc',
            orderColumn: orderData.orderColumn || orderData.field as string,
        }));
    };

    const fetchAllForExport = useCallback(async () => {
        const result = await AlarmsService.getAlarmTriggersList({
            ...criteria,
            page: 1,
            itemsPerPage: MAX_PAGINATION_ITEMS_PER_PAGE,
        });
        return result.items;
    }, [criteria]);

    const dataTableColumns: DataTableColumn<AlarmTriggerDto>[] = useMemo(() => {
        const cols: DataTableColumn<AlarmTriggerDto>[] = [
            {
                field: 'vehiclePhotoUrl' as keyof AlarmTriggerDto,
                name: t('common.image') as string,
                width: 80,
                hideOrderButton: true,
                excludeFromExport: true,
                renderCell: row => (
                    <div className={styles.contentImage} style={{ width: '5rem', height: '3rem' }}>
                        <div className={styles.img} style={{
                            width: '5rem', height: '3rem',
                            backgroundImage: row?.vehiclePhotoUrl
                                ? 'url(' + row.vehiclePhotoUrl + '?_=' + imageCacheKey.current + ')'
                                : row?.vehicleSegmentDefaultPhotoUrl
                                    ? 'url(' + row.vehicleSegmentDefaultPhotoUrl + '?_=' + imageCacheKey.current + ')'
                                    : 'url(' + DefaultAssetImage + ')'
                        }} />
                    </div>
                ),
            },
            {
                field: 'vehicleRegistrationNumber' as keyof AlarmTriggerDto,
                name: t('alarms.notifications_tab.table.registration') as string,
                orderColumn: 'v.license_plate',
                width: 140,
                renderCell: row => <><span className={styles.licensePlate}>{row.vehicleRegistrationNumber}</span></>,
            },
            {
                field: 'vehicleBrand' as keyof AlarmTriggerDto,
                name: t('alarms.notifications_tab.table.brand') as string,
                width: 140,
                hideOrderButton: true,
                renderCell: row => <>{row.vehicleBrand || '-'}</>,
            },
            {
                field: 'vehicleModel' as keyof AlarmTriggerDto,
                name: t('alarms.notifications_tab.table.model') as string,
                width: 140,
                hideOrderButton: true,
                renderCell: row => <>{row.vehicleModel || '-'}</>,
            },
            {
                field: 'triggerDate' as keyof AlarmTriggerDto,
                name: t('alarms.notifications_tab.table.date') as string,
                orderColumn: 'ti.date',
                width: 180,
                exportValue: row => row.triggerDate ? moment(row.triggerDate).format(DATE_TIME_FORMAT_DEFAULT) : '',
                renderCell: row => <DateFormat value={row.triggerDate} format={DATE_TIME_FORMAT_DEFAULT} />,
            },
            {
                field: 'latitude' as keyof AlarmTriggerDto,
                name: t('alarms.notifications_tab.table.address') as string,
                width: 200,
                hideOrderButton: true,
                hideOn: ['sm', 'md', 'lg'],
                exportValue: row => row.latitude && row.longitude ? `${row.latitude},${row.longitude}` : '',
                renderCell: row => (
                    <span className={styles.labelAddress}>
                        <FaMapMarkerAlt className={styles.pin} />
                        <a href={`https://www.google.com/maps/search/?api=1&query=${row.latitude},${row.longitude}`}>{t('alarms.notifications_tab.table.open_location')}
                            <span className={styles.linkIcon}>
                                <FaExternalLinkAlt />
                            </span>
                            <span className={styles.small}>({`${row.latitude},${row.longitude}`})</span>
                        </a>
                    </span>
                ),
            },
        ];
        return cols;
    }, [t]);

    return (
        <ScreenTitle title={t('alarms.notifications_tab.title')}>
            <div className={styles.toolbarRow}>
                <ExportListingButton exportRef={exportListingRef} />
            </div>
            <div style={{ marginTop: '0.5rem' }}>
            <DataTableWithSidebar
                cacheKey="ALARM_TRIGGERS_TABLE"
                columns={dataTableColumns}
                rows={triggers}
                defaultOrder={order}
                onOrderChange={handleOrderChange}
                onRowId={row => row.id}
                infiniteScroll={{
                    isLoading: isLoadingMore,
                    hasMore: getHasMore(triggers.length, totalItems),
                    onLoadMore: getOnLoadMore(loadMore),
                    hasMorePrevious: getHasMorePrevious(),
                    isLoadingPrevious,
                    onLoadPrevious: getOnLoadPrevious(loadPrevious),
                }}
                exportExcel={{
                    fetchAll: fetchAllForExport,
                    fileName: t('notifications.title') as string,
                }}
                exportRef={exportListingRef}
                hideExportInSidebar={true}
            />
            </div>
        </ScreenTitle>
    );
};

export default NotificationsTab;
